import { useIsMobile } from '@/hooks/use-IsMobile';
import { ReviewAssessment } from '@/components/ats/job-stage-actions/actions/assessment/ReviewAssessment';
import { ReviewAssessmentMobile } from '@/components/ats/job-stage-actions/actions/assessment/ReviewAssessmentMobile';
import { ReviewScoreCard } from '@/components/ats/job-stage-actions/actions/review/review-activity/ReviewScoreCard';
import { ReviewScoreCardMobile } from '@/components/ats/job-stage-actions/actions/review/review-activity/ReviewScoreCardMobile';
import { REVIEW_KINDS } from './contextualScorecardKinds';

interface ContextualScorecardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Activity kind of the selected scorecard (review / assessment family). */
  kind: string;
  /** Application context — needs `id` (and `ats_job` for review job-status). */
  application: any;
  /** The review / assessment record id the drawer fetches scorecards for. */
  recordId?: string;
  /** Specific scorecard to expand within the record's list. */
  scorecardId?: string;
  /** View-only vs editable. */
  viewSc?: boolean;
  canFillForOthers?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  showNavButtons?: boolean;
  isFirstScorecard?: boolean;
  isLastScorecard?: boolean;
}

/**
 * Renders the activity-type-aware scorecard drawer for review and assessment
 * activities, mirroring the Dashboard → Tasks dispatch. Branches to the mobile
 * variant when appropriate. Interview / transferred scorecards are handled by
 * the caller via CollectScoreCardDrawerV1 (use `isContextualScorecardKind`).
 */
export const ContextualScorecardDrawer = ({
  isOpen,
  onClose,
  onSuccess,
  kind,
  application,
  recordId,
  scorecardId,
  viewSc = false,
  canFillForOthers = false,
  onNext,
  onPrev,
  showNavButtons = false,
  isFirstScorecard = false,
  isLastScorecard = false,
}: ContextualScorecardDrawerProps) => {
  const isMobile = useIsMobile();
  // View mode shows every reviewer's card; submit mode narrows to the current user's.
  const showOnlyCurrUser = !viewSc;
  const isReview = REVIEW_KINDS.includes(kind);

  if (isReview && isMobile) {
    return (
      <ReviewScoreCardMobile
        isOpen={isOpen}
        onClose={onClose}
        application={application}
        reviewId={recordId}
        expandOnly={scorecardId}
        showOnlyCurrUser={showOnlyCurrUser}
        onSuccess={onSuccess}
      />
    );
  }

  if (isReview) {
    return (
      <ReviewScoreCard
        isOpen={isOpen}
        onClose={onClose}
        application={application}
        reviewId={recordId}
        expandOnly={scorecardId}
        viewSc={viewSc}
        showOnlyCurrUser={showOnlyCurrUser}
        canFillForOthers={canFillForOthers}
        onSuccess={onSuccess}
        onNext={onNext}
        onPrev={onPrev}
        showNavButtons={showNavButtons}
        isFirstScorecard={isFirstScorecard}
        isLastScorecard={isLastScorecard}
      />
    );
  }

  if (isMobile) {
    return (
      <ReviewAssessmentMobile
        isOpen={isOpen}
        onClose={onClose}
        application={application}
        assessmentId={recordId}
        showOnlyCurrUser={showOnlyCurrUser}
        canFillForOthers={canFillForOthers}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <ReviewAssessment
      isOpen={isOpen}
      onClose={onClose}
      application={application}
      assessmentId={recordId}
      scorecardId={scorecardId}
      showOnlyCurrUser={showOnlyCurrUser}
      canFillForOthers={canFillForOthers}
      onSuccess={onSuccess}
      onNext={onNext}
      onPrev={onPrev}
      showNavButtons={showNavButtons}
      isFirstScorecard={isFirstScorecard}
      isLastScorecard={isLastScorecard}
    />
  );
};
